import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import { useMemo, useState } from "react";

interface WasteCodeGridProps {
  codes: string[];
  title: string;
  searchTerm: string;
  specificSearchTerm: string;
  itemsPerPage: number;
  keyPrefix: string;
  emptyStateMessage: {
    withSearch: string;
    withoutSearch: string;
  };
  fullWidth?: boolean;
}

export const WasteCodeGrid = ({
  codes,
  title,
  searchTerm,
  specificSearchTerm,
  itemsPerPage,
  keyPrefix,
  emptyStateMessage,
  fullWidth = false,
}: WasteCodeGridProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);

  // Filter codes based on both global search term and specific search term
  const filteredCodes = useMemo(() => {
    if (!codes) return [];
    return codes.filter((code) => {
      const lowerCode = code.toLowerCase();
      const matchesGlobal =
        !searchTerm || lowerCode.includes(searchTerm.toLowerCase());
      const matchesSpecific =
        !specificSearchTerm ||
        lowerCode.includes(specificSearchTerm.toLowerCase());
      return matchesGlobal && matchesSpecific;
    });
  }, [codes, searchTerm, specificSearchTerm]);

  const paginatedCodes = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCodes.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCodes, page, rowsPerPage]);

  // Reset pagination when search or codes change
  useMemo(() => {
    setPage(0);
  }, [searchTerm, specificSearchTerm, codes]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Grid
      size={{
        xs: 12,
        md: fullWidth ? 12 : 6,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="body1" fontWeight={500}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {paginatedCodes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {searchTerm || specificSearchTerm
                ? emptyStateMessage.withSearch
                : emptyStateMessage.withoutSearch}
            </Typography>
          ) : (
            <>
              <TableContainer sx={{ flex: 1 }}>
                <Table size="small">
                  <TableBody>
                    {paginatedCodes.map((code, index) => (
                      <TableRow
                        key={`${keyPrefix}-${code}-${index}`}
                        hover
                        sx={{
                          cursor: "default",
                        }}
                      >
                        <TableCell sx={{ px: 2 }}>{code}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ borderTop: "1px", borderColor: "divider" }} />
            </>
          )}

          {filteredCodes.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredCodes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              size="small"
            />
          )}
        </Box>
      </Paper>
    </Grid>
  );
};
